import { useEffect } from "react";
import { Beaker, ChevronRight, ShieldCheck, X } from "lucide-react";

interface PennyModeChooserProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectBeta: () => void;
  onSelectPro: () => void;
}

export default function PennyModeChooser({ isOpen, onClose, onSelectBeta, onSelectPro }: PennyModeChooserProps) {
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-end justify-center sm:items-center" role="presentation">
      <button className="absolute inset-0 bg-black/45 backdrop-blur-[2px] sheet-scrim-in" onClick={onClose} aria-label="Close penny mode chooser" />
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="penny-mode-title"
        className="relative w-full rounded-t-3xl bg-white px-4 pb-[max(20px,env(safe-area-inset-bottom))] pt-3 shadow-2xl dark:bg-dark-800 sm:max-w-md sm:rounded-3xl sm:p-5 sheet-panel-in-sm"
      >
        <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-gray-200 dark:bg-dark-600 sm:hidden" />
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="penny-mode-title" className="text-lg font-bold text-gray-950 dark:text-white">Choose your penny view</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-dark-300">Two ways to hunt Home Depot penny items.</p>
          </div>
          <button onClick={onClose} className="rounded-full p-2 text-gray-400 hover:bg-gray-100 dark:hover:bg-dark-700" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <button onClick={onSelectBeta} className="flex w-full items-center gap-3 rounded-2xl border border-violet-200 bg-violet-50/70 p-4 text-left transition-colors hover:bg-violet-100 dark:border-violet-800 dark:bg-violet-950/30 dark:hover:bg-violet-950/50">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-violet-600 text-white"><Beaker className="h-5 w-5" /></span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2 font-bold text-gray-950 dark:text-white">Penny Beta <span className="rounded-full bg-violet-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">New</span></span>
              <span className="mt-1 block text-xs leading-5 text-gray-600 dark:text-dark-300">Early candidates from the latest DealHawk store scans. Filter physical and sellable inventory.</span>
            </span>
            <ChevronRight className="h-5 w-5 shrink-0 text-violet-500" />
          </button>

          <button onClick={onSelectPro} className="flex w-full items-center gap-3 rounded-2xl border border-gray-200 p-4 text-left transition-colors hover:bg-gray-50 dark:border-dark-600 dark:hover:bg-dark-700">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"><ShieldCheck className="h-5 w-5" /></span>
            <span className="min-w-0 flex-1">
              <span className="font-bold text-gray-950 dark:text-white">Penny Pro</span>
              <span className="mt-1 block text-xs leading-5 text-gray-600 dark:text-dark-300">The current prediction feed with readiness timing and nearby-store checks.</span>
            </span>
            <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
          </button>
        </div>

        <p className="mt-4 text-center text-[11px] leading-4 text-gray-400 dark:text-dark-400">Candidates and predictions are not guaranteed penny prices. Verify in store.</p>
      </section>
    </div>
  );
}
